if(!customElements.get('wlsn-5050')) {

  class WLSN5050 extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        mediaBlock: 'js-wlsn-5050-media-block',
        mediaPosition: 'js-wlsn-5050-media-position-small-screens',
      }
      this.accordionAutoplayMs = 3000;
      this.accordionVideoAutoplayMs = 5000;

    }

    get elements() {
      return {
        mediaBlock: this.querySelector(`.${this.classes.mediaBlock}`),
        mediaPosition: this.querySelector(`.${this.classes.mediaPosition}`),
        imageWrapper: this.querySelector(`.${this.classes.mediaBlock} .wlsn-image-wrapper`),
      }
    }

    connectedCallback() {
      this.initMediaSwap();
      this.initAccordionImageSync();
      this.initAccordionAutoplay();
    }

    disconnectedCallback() {
      this.stopAccordionAutoplay();
      if (this._swapImageTimeout) {
        clearTimeout(this._swapImageTimeout);
        this._swapImageTimeout = null;
      }
      if (this.mediaQuery && this.handleMediaChange) {
        if (this.mediaQuery.removeEventListener) {
          this.mediaQuery.removeEventListener('change', this.handleMediaChange);
        } else {
          // Fallback for older browsers
          this.mediaQuery.removeListener(this.handleMediaChange);
        }
      }
      if (this.boundAccordionOpened) this.removeEventListener('wlsn:accordion:opened', this.boundAccordionOpened);
      if (this.boundAccordionClosed) this.removeEventListener('wlsn:accordion:closed', this.boundAccordionClosed);
    }

    initAccordionImageSync() {
      const imageWrapper = this.elements.imageWrapper;
      if (!imageWrapper) return;
      this.fallbackImageContent = imageWrapper.innerHTML;
      this.boundAccordionOpened = this.onAccordionOpened.bind(this);
      this.boundAccordionClosed = this.onAccordionClosed.bind(this);
      this.addEventListener('wlsn:accordion:opened', this.boundAccordionOpened);
      this.addEventListener('wlsn:accordion:closed', this.boundAccordionClosed);
    }

    onAccordionOpened(e) {
      const detail = e.detail || {};
      if (detail.fromDefaultOpen) return;
      const { item } = detail;
      const imageWrapper = this.elements.imageWrapper;
      if (!imageWrapper) return;
      const accordionImageEl = item && item.querySelector('.js-accordion-image');
      const accordionVideo = accordionImageEl && accordionImageEl.querySelector('video');
      const accordionImg = accordionImageEl && accordionImageEl.querySelector('img');
      if (accordionVideo) {
        const videoClone = accordionVideo.cloneNode(true);
        videoClone.removeAttribute('id');
        videoClone.className = 'wlsn-image';
        videoClone.muted = true;
        videoClone.setAttribute('playsinline', '');
        this.swapImageContent(() => {
          imageWrapper.innerHTML = '';
          imageWrapper.appendChild(videoClone);
          try { videoClone.load(); } catch (err) {}
          const playPromise = videoClone.play && videoClone.play();
          if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(() => {});
          }
        });
      } else if (accordionImg && accordionImg.src) {
        const src = this.getHighResImageUrl(accordionImg.src);
        const img = document.createElement('img');
        img.src = src;
        img.alt = accordionImg.alt || '';
        img.className = 'wlsn-image';
        this.swapImageContent(() => {
          imageWrapper.innerHTML = '';
          imageWrapper.appendChild(img);
        });
      } else {
        this.swapImageContent(() => this.restoreFallbackImage());
      }
    }

    onAccordionClosed() {
      this.swapImageContent(() => this.restoreFallbackImage());
    }

    swapImageContent(doSwap) {
      const imageWrapper = this.elements.imageWrapper;
      if (!imageWrapper) return;
      const duration = 300;
      imageWrapper.classList.add('is-changing');
      this._swapImageTimeout = setTimeout(() => {
        this._swapImageTimeout = null;
        doSwap();
        imageWrapper.classList.remove('is-changing');
      }, duration);
    }

    restoreFallbackImage() {
      const imageWrapper = this.elements.imageWrapper;
      if (this.fallbackImageContent && imageWrapper) {
        imageWrapper.innerHTML = this.fallbackImageContent;
      }
    }

    getHighResImageUrl(url) {
      if (!url) return url;
      return url.replace(/([?&])width=\d+/, '$1width=2400');
    }

    initAccordionAutoplay() {
      this.stopAccordionAutoplay();
      const accordion = this.querySelector('wlsn-accordion');
      if (!accordion) return;
      const items = accordion.querySelectorAll('wlsn-accordion-item');
      if (!items.length) return;

      this._accordionAutoplayEl = accordion;
      this.boundAccordionAutoplayReset = this.onAccordionAutoplayInteraction.bind(this);
      this.addEventListener('wlsn:accordion:opened', this.boundAccordionAutoplayReset);
      this.addEventListener('wlsn:accordion:closed', this.boundAccordionAutoplayReset);

      this.scheduleAccordionAutoplayTick();
    }

    /**
     * Delay before advancing autoplay. Video panels use a longer window so clips can finish.
     * @param {HTMLElement} [item] - Open accordion item (when known)
     * @returns {number}
     */
    getAccordionAutoplayDelay(item) {
      if (item?.querySelector('.js-accordion-image video')) {
        return this.accordionVideoAutoplayMs;
      }
      return this.accordionAutoplayMs;
    }

    /**
     * (Re)starts the delay until the next autoplay step. Called on interval and after any
     * open/close on the autoplay accordion so manual interaction gets a full fresh window.
     * @param {HTMLElement} [item] - Open accordion item; when omitted, uses the currently expanded item
     */
    scheduleAccordionAutoplayTick(item) {
      this.clearAccordionAutoplayTimeout();
      if (!this._accordionAutoplayEl) return;

      const openItem =
        item ||
        this._accordionAutoplayEl.querySelector('wlsn-accordion-item[data-expanded]');
      const delay = this.getAccordionAutoplayDelay(openItem);

      this._accordionAutoplayTimeout = setTimeout(() => {
        this._accordionAutoplayTimeout = null;
        this.advanceAccordionAutoplay(this._accordionAutoplayEl);
        // Next tick is scheduled by onAccordionAutoplayInteraction from the opened/closed event.
      }, delay);
    }

    onAccordionAutoplayInteraction(e) {
      if (!this._accordionAutoplayEl || e.target !== this._accordionAutoplayEl) return;
      const item = e.type === 'wlsn:accordion:opened' ? e.detail?.item : undefined;
      this.scheduleAccordionAutoplayTick(item);
    }

    clearAccordionAutoplayTimeout() {
      if (this._accordionAutoplayTimeout) {
        clearTimeout(this._accordionAutoplayTimeout);
        this._accordionAutoplayTimeout = null;
      }
    }

    stopAccordionAutoplay() {
      this.clearAccordionAutoplayTimeout();
      if (this.boundAccordionAutoplayReset) {
        this.removeEventListener('wlsn:accordion:opened', this.boundAccordionAutoplayReset);
        this.removeEventListener('wlsn:accordion:closed', this.boundAccordionAutoplayReset);
        this.boundAccordionAutoplayReset = null;
      }
      this._accordionAutoplayEl = null;
    }

    /**
     * Cycles open accordion panel every interval via trigger click so wlsn-accordion
     * runs its own logic and image sync events stay correct.
     */
    advanceAccordionAutoplay(accordion) {
      const items = [...accordion.querySelectorAll('wlsn-accordion-item')];
      if (!items.length) return;

      const triggers = items
        .map((item) => item.querySelector('wlsn-accordion-trigger'))
        .filter(Boolean);
      if (!triggers.length) return;

      if (items.length === 1) {
        triggers[0].click();
        return;
      }

      const openIdx = items.findIndex((el) => el.hasAttribute('data-expanded'));
      const nextIdx = openIdx >= 0 ? (openIdx + 1) % items.length : 0;
      const closeOthers = accordion.hasAttribute('data-close-others');

      if (closeOthers) {
        triggers[nextIdx].click();
        return;
      }

      if (openIdx >= 0) {
        triggers[openIdx].click();
        requestAnimationFrame(() => {
          requestAnimationFrame(() => triggers[nextIdx].click());
        });
      } else {
        triggers[nextIdx].click();
      }
    }

    initMediaSwap() {
      const { mediaPosition, mediaBlock } = this.elements;
      
      if (!mediaPosition || !mediaBlock) {
        return;
      }

      // Store original positions for restoration
      if (!this.originalPositions) {
        this.originalPositions = {
          mediaBlock: {
            parent: mediaBlock.parentNode,
            nextSibling: mediaBlock.nextSibling
          },
          mediaPosition: {
            parent: mediaPosition.parentNode,
            nextSibling: mediaPosition.nextSibling
          }
        };
      }

      // Create media query for screens smaller than 1023px
      this.mediaQuery = window.matchMedia('(max-width: 1023px)');
      
      // Handle media query changes
      this.handleMediaChange = (e) => {
        if (e.matches) {
          // Screen is smaller than 1023px - replace mediaPosition with mediaBlock
          this.swapMediaElements();
        } else {
          // Screen is 1024px or larger - restore original positions
          this.restoreMediaElements();
        }
      };

      // Set initial state
      this.handleMediaChange(this.mediaQuery);
      
      // Listen for changes (using addEventListener for modern browsers)
      if (this.mediaQuery.addEventListener) {
        this.mediaQuery.addEventListener('change', this.handleMediaChange);
      } else {
        // Fallback for older browsers
        this.mediaQuery.addListener(this.handleMediaChange);
      }
    }

    swapMediaElements() {
      const { mediaPosition, mediaBlock } = this.elements;
      
      if (!mediaPosition || !mediaBlock || this.isSwapped) {
        return;
      }

      // Get the position where mediaPosition is located
      const mediaPositionParent = mediaPosition.parentNode;
      const mediaPositionNextSibling = mediaPosition.nextSibling;

      // Move mediaBlock to replace mediaPosition
      if (mediaPositionNextSibling) {
        mediaPositionParent.insertBefore(mediaBlock, mediaPositionNextSibling);
      } else {
        mediaPositionParent.appendChild(mediaBlock);
      }

      // Hide original mediaPosition
      mediaPosition.style.display = 'none';
      
      this.isSwapped = true;
    }

    restoreMediaElements() {
      const { mediaPosition, mediaBlock } = this.elements;
      
      if (!this.isSwapped || !this.originalPositions) {
        return;
      }

      // Restore mediaBlock to original position
      const originalBlockParent = this.originalPositions.mediaBlock.parent;
      const originalBlockNextSibling = this.originalPositions.mediaBlock.nextSibling;
      
      if (originalBlockNextSibling) {
        originalBlockParent.insertBefore(mediaBlock, originalBlockNextSibling);
      } else {
        originalBlockParent.appendChild(mediaBlock);
      }

      // Restore mediaPosition visibility
      if (mediaPosition) {
        mediaPosition.style.display = '';
      }
      
      this.isSwapped = false;
    }
  }
  customElements.define('wlsn-5050', WLSN5050);
}